// ========================================
// Tutorías (profesor y alumno) — el diseño de siempre, pulido (D, oct. 2026):
// cabecera oscura con lo que te toca; el alumno pide tutoría a la izquierda y el
// profesor tiene su calendario, sus próximas citas y sus horas para la asesoría;
// a la derecha las solicitudes, primero lo que te toca hacer.
// ========================================
const TutoriasView = {
    _tutorias: [],
    _professores: [],
    _currentMonthDate: new Date(),
    _advMonth: null, // YYYY-MM de «Horas para la asesoría»
    _filter: 'open', // 'open' (abiertas y próximas) | 'history'

    _pad(n) { return String(n).padStart(2, '0'); },
    _iso(d) { return `${d.getFullYear()}-${this._pad(d.getMonth() + 1)}-${this._pad(d.getDate())}`; },
    _today() { return this._iso(new Date()); },
    _esc(s) { return Utils.sanitizeHTML(String(s == null ? '' : s)); },
    _day(iso, opts = { weekday: 'long', day: 'numeric', month: 'long' }) {
        const [y, m, d] = String(iso || '').split('-').map(Number);
        if (!y || !m || !d) return String(iso || '');
        return new Date(y, m - 1, d).toLocaleDateString('es-ES', opts);
    },
    _isStudent() { return AuthManager.getCurrentUser()?.role === 'student'; },

    // Qué toca con cada tutoría desde este lado: 'todo' (te toca), 'wait', 'next' (confirmada por venir) o 'history'.
    _bucket(t) {
        const student = this._isStudent();
        if (t.status === 'cancelled') return 'history';
        if (t.status === 'confirmed') return (t.confirmedDate || '') >= this._today() ? 'next' : 'history';
        const mine = student ? ['proposed_1', 'proposed_3'] : ['pending', 'rejected_1'];
        return mine.includes(t.status) ? 'todo' : 'wait';
    },

    changeMonth(offset) {
        this._currentMonthDate.setMonth(this._currentMonthDate.getMonth() + offset);
        this._repaint();
    },
    setFilter(value) { this._filter = value === 'history' ? 'history' : 'open'; this._repaint(); },
    setAdvMonth(value) { if (/^\d{4}-\d{2}$/.test(value)) { this._advMonth = value; this._repaint(); } },
    _repaint() {
        const contentArea = document.getElementById('content-area');
        if (contentArea) SafeHTML.set(contentArea, this.render());
    },

    renderCalendar() {
        const year = this._currentMonthDate.getFullYear(), month = this._currentMonthDate.getMonth();
        const label = new Date(year, month, 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
        const byDay = {};
        this._tutorias.filter(t => t.status === 'confirmed' && t.confirmedDate).forEach(t => { (byDay[t.confirmedDate] ||= []).push(t); });
        const offset = (new Date(year, month, 1).getDay() + 6) % 7;
        const total = new Date(year, month + 1, 0).getDate();
        const today = this._today();
        let cells = ['L', 'M', 'X', 'J', 'V', 'S', 'D'].map(d => `<span class="tv-dow">${d}</span>`).join('');
        for (let i = 0; i < offset; i++) cells += '<span></span>';
        for (let day = 1; day <= total; day++) {
            const iso = `${year}-${this._pad(month + 1)}-${this._pad(day)}`;
            const list = byDay[iso] || [];
            const title = list.map(t => `${t.confirmedTime || ''} · ${this._isStudent() ? t.professorName : t.studentName}`).join('\n');
            cells += `<span class="tv-cal-day ${iso === today ? 'is-today' : ''} ${list.length ? 'has-tut' : ''}" ${list.length ? `title="${this._esc(title)}"` : ''}>${day}${list.length ? '<i aria-hidden="true"></i>' : ''}</span>`;
        }
        return `
            <div class="tv-cal-head">
                <b>${this._esc(label.charAt(0).toUpperCase() + label.slice(1))}</b>
                <button type="button" class="tv-btn tv-btn-sm" aria-label="Mes anterior" ${UIEvents.attr("click", function(event,captured){TutoriasView.changeMonth(-1);}, [])}><i class="fas fa-chevron-left"></i></button>
                <button type="button" class="tv-btn tv-btn-sm" aria-label="Mes siguiente" ${UIEvents.attr("click", function(event,captured){TutoriasView.changeMonth(1);}, [])}><i class="fas fa-chevron-right"></i></button>
            </div>
            <div class="tv-cal">${cells}</div>`;
    },

    renderUpcoming() {
        const next = this._tutorias.filter(t => this._bucket(t) === 'next')
            .sort((a, b) => `${a.confirmedDate} ${a.confirmedTime}`.localeCompare(`${b.confirmedDate} ${b.confirmedTime}`)).slice(0, 5);
        return `
            <div class="tv-block">
                <span class="tv-label">Próximas citas</span>
                ${next.length ? next.map(t => `<div class="tv-next"><b>${this._esc(this._day(t.confirmedDate, { weekday: 'short', day: 'numeric' }))} ${this._esc(t.confirmedTime || '')}</b><span>${this._esc(t.studentName)}</span></div>`).join('')
                    : '<p class="tv-muted">No tienes citas confirmadas por venir.</p>'}
            </div>`;
    },

    // Una hora por tutoría confirmada del mes (la misma regla que la página de RRHH).
    renderAdvisory() {
        const confirmed = this._tutorias.filter(t => t.status === 'confirmed' && t.confirmedDate);
        const current = this._today().substring(0, 7);
        const months = [...new Set([current, ...confirmed.map(t => t.confirmedDate.substring(0, 7))])].sort().reverse();
        if (!months.includes(this._advMonth)) this._advMonth = current;
        const inMonth = confirmed.filter(t => t.confirmedDate.startsWith(this._advMonth));
        const monthName = m => { const label = new Date(Number(m.slice(0, 4)), Number(m.slice(5, 7)) - 1, 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' }); return label.charAt(0).toUpperCase() + label.slice(1); };
        const sent = inMonth.length > 0 && inMonth.every(t => t.sentToAdvisory);
        const state = !inMonth.length ? ['', 'Sin tutorías confirmadas este mes'] : sent ? ['is-ok', '✓ Enviadas a la asesoría'] : ['is-warn', 'Pendiente de enviar a la asesoría'];
        const n = inMonth.length;
        return `
            <div class="tv-block">
                <div class="tv-adv-head">
                    <span class="tv-label">Horas para la asesoría</span>
                    <select class="tv-input" aria-label="Mes" ${UIEvents.attr("change", function(event,captured){TutoriasView.setAdvMonth(this.value);}, [])}>
                        ${months.map(m => `<option value="${m}" ${m === this._advMonth ? 'selected' : ''}>${this._esc(monthName(m))}</option>`).join('')}
                    </select>
                </div>
                <div class="tv-adv-figure"><b>${n} h</b><span>${n} ${n === 1 ? 'tutoría confirmada' : 'tutorías confirmadas'}</span></div>
                <span class="tv-pill ${state[0]}">${state[1]}</span>
                <small class="tv-muted">Cuenta una hora por tutoría confirmada del mes. RRHH la marca como enviada cuando la pasa a la asesoría para tu nómina.</small>
            </div>`;
    },

    render() {
        const user = AuthManager.getCurrentUser();
        if (!user) return '<div class="view-container">Usuario no autenticado</div>';
        const isStudent = this._isStudent();
        const counts = { todo: 0, wait: 0, next: 0 };
        this._tutorias.forEach(t => { const b = this._bucket(t); if (b in counts) counts[b]++; });
        return `
        <div class="view-container dnr-proposal tv-view" style="padding-top: 1rem;">
            <style ${SafeHTML.styleAttr}>
                .tv-view { gap: 0.85rem; }
                .tv-hero { display: flex; flex-wrap: wrap; align-items: center; gap: 14px; padding: 1.1rem 1.35rem; border-radius: 16px; background: var(--dnr-ink); color: #fff; }
                .tv-hero > div:first-child { flex: 1; min-width: 240px; }
                .tv-hero h1 { margin: 0; font-family: var(--dnr-display); font-weight: 400; font-size: 2.4rem; letter-spacing: 0.02em; line-height: 1; color: #fff !important; }
                .tv-hero p { margin: 4px 0 0; font-size: 0.88rem; color: #A9C9C2; }
                .tv-counts { display: flex; gap: 8px; }
                .tv-count { padding: 8px 14px; border-radius: 12px; background: rgba(255,255,255,.08); text-align: center; min-width: 78px; }
                .tv-count b { display: block; font-family: var(--dnr-display); font-weight: 400; font-size: 1.9rem; line-height: 1; }
                .tv-count span { font-size: 0.75rem; color: #C9D8D4; }
                .tv-count.is-todo { background: rgba(240,184,113,.16); } .tv-count.is-todo b { color: #F0B871; }
                .tv-count.is-next { background: rgba(127,211,195,.14); } .tv-count.is-next b { color: #7FD3C3; }
                .tv-layout { display: grid; grid-template-columns: 340px minmax(0, 1fr); gap: 0.85rem; align-items: start; }
                .tv-card { background: var(--dnr-surface); border: 1px solid var(--dnr-line); border-radius: 16px; box-shadow: var(--dnr-shadow-card); padding: 1rem; }
                .tv-card h2, .tv-list-head h2 { margin: 0; font-family: var(--dnr-display); font-weight: 400; font-size: 1.6rem; letter-spacing: 0.03em; line-height: 1; color: var(--dnr-ink); }
                .tv-label { font: 700 0.68rem var(--dnr-mono); letter-spacing: 0.08em; text-transform: uppercase; color: var(--dnr-ink-mute); }
                .tv-muted { margin: 0; font-size: 0.8rem; color: var(--dnr-ink-mute); }
                .tv-input { min-height: 36px; border: 1px solid var(--dnr-line); border-radius: 9px; padding: 0 8px; font-size: 0.88rem; font-family: inherit; background: var(--dnr-surface); color: var(--dnr-ink); }
                .tv-input:focus { outline: none; border-color: var(--dnr-teal-500); box-shadow: 0 0 0 3px var(--dnr-teal-100); }
                .tv-form { display: flex; flex-direction: column; gap: 0.75rem; margin-top: 0.75rem; }
                .tv-form label { font-size: 0.8rem; font-weight: 700; color: var(--dnr-ink); display: flex; flex-direction: column; gap: 4px; }
                .tv-form textarea { padding: 8px; resize: vertical; }
                .tv-btn { min-height: 36px; padding: 0 12px; border: 1px solid var(--dnr-line); border-radius: 9px; background: var(--dnr-surface); color: var(--dnr-ink); font-weight: 700; font-size: 0.82rem; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 6px; font-family: inherit; }
                .tv-btn:hover { border-color: var(--dnr-teal-400); }
                .tv-btn.is-primary { background: var(--dnr-teal-600); border-color: var(--dnr-teal-600); color: #fff; }
                .tv-btn.is-big { min-height: 42px; font-size: 0.9rem; }
                .tv-btn.is-danger { color: #8E2A21; }
                .tv-btn-sm { min-height: 30px; padding: 0 9px; }
                .tv-cal-head { display: flex; align-items: center; gap: 6px; margin-bottom: 8px; }
                .tv-cal-head b { flex: 1; color: var(--dnr-ink); }
                .tv-cal { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); gap: 3px; text-align: center; }
                .tv-dow { font: 700 0.62rem var(--dnr-mono); color: var(--dnr-ink-mute); padding: 2px 0; }
                .tv-cal-day { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 34px; border-radius: 8px; font-size: 0.8rem; font-weight: 700; color: var(--dnr-ink); }
                .tv-cal-day i { width: 5px; height: 5px; border-radius: 50%; background: var(--dnr-teal-600); margin-top: 2px; }
                .tv-cal-day.has-tut { background: var(--dnr-teal-100); color: var(--dnr-teal-700); cursor: help; }
                .tv-cal-day.is-today { background: var(--dnr-ink); color: #fff; }
                .tv-cal-day.is-today i { background: #7FD3C3; }
                .tv-block { margin-top: 0.85rem; padding-top: 0.85rem; border-top: 1px solid var(--dnr-line); display: flex; flex-direction: column; gap: 6px; }
                .tv-next { display: flex; gap: 8px; font-size: 0.88rem; color: var(--dnr-ink); }
                .tv-next b { font: 700 0.75rem var(--dnr-mono); color: var(--dnr-teal-700); min-width: 88px; text-transform: capitalize; }
                .tv-adv-head { display: flex; align-items: center; gap: 8px; }
                .tv-adv-head .tv-label { flex: 1; }
                .tv-adv-figure { display: flex; align-items: baseline; gap: 8px; }
                .tv-adv-figure b { font-family: var(--dnr-display); font-weight: 400; font-size: 2.4rem; line-height: 1; color: var(--dnr-ink); }
                .tv-adv-figure span { font-size: 0.88rem; color: var(--dnr-ink); }
                .tv-pill { align-self: flex-start; font-size: 0.75rem; font-weight: 700; padding: 4px 10px; border-radius: 999px; background: var(--dnr-surface-sunken); color: var(--dnr-ink-mute); white-space: nowrap; }
                .tv-pill.is-ok { background: var(--dnr-teal-100); color: var(--dnr-teal-700); }
                .tv-pill.is-warn { background: var(--dnr-amber-100); color: #6B3A0B; }
                .tv-pill.is-bad { background: #FBE4E1; color: #8E2A21; }
                .tv-list { display: flex; flex-direction: column; gap: 0.6rem; }
                .tv-list-head { display: flex; align-items: center; gap: 8px; }
                .tv-list-head h2 { flex: 1; }
                .tv-item { background: var(--dnr-surface); border: 1px solid var(--dnr-line); border-radius: 14px; padding: 0.9rem 1rem; display: flex; flex-direction: column; gap: 0.6rem; }
                .tv-item.is-todo { border-color: #EBD3AE; }
                .tv-item-head { display: flex; align-items: flex-start; gap: 10px; }
                .tv-item-head > div { flex: 1; }
                .tv-item-head b { font-size: 1rem; color: var(--dnr-ink); }
                .tv-item-head small { display: block; font-size: 0.75rem; color: var(--dnr-ink-mute); }
                .tv-reason { margin: 0; padding: 8px 12px; border-radius: 10px; background: var(--dnr-surface-sunken); font-size: 0.88rem; color: var(--dnr-ink); }
                .tv-act { display: flex; flex-direction: column; gap: 8px; padding: 10px 12px; border-radius: 10px; background: #FAFCFB; }
                .tv-act.is-todo { background: #FDF7EE; }
                .tv-act.is-next { background: var(--dnr-teal-100); }
                .tv-act.is-cancelled { background: #FDF3F1; }
                .tv-act > b { font-size: 0.9rem; color: var(--dnr-ink); }
                .tv-row { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
                .tv-row .tv-input.tv-time { width: 104px; }
                .tv-opt-label { width: 70px; font-size: 0.75rem; font-weight: 700; color: #7A4210; }
                .tv-empty { text-align: center; padding: 2.5rem 1.5rem; background: var(--dnr-surface); border: 1px dashed var(--dnr-line-strong); border-radius: 16px; color: var(--dnr-ink-mute); }
                .tv-empty p { margin: 0; }
                @media (max-width: 960px) {
                    .tv-layout { grid-template-columns: minmax(0, 1fr); }
                    .tv-counts { width: 100%; }
                    .tv-count { flex: 1; }
                }
            </style>
            <header class="tv-hero">
                <div>
                    <h1>${isStudent ? 'Mis tutorías' : 'Tutorías'}</h1>
                    <p>${isStudent ? 'Pide y gestiona tus reuniones con los profesores.' : 'Responde a las solicitudes de tutoría de tus alumnos.'}</p>
                </div>
                <div class="tv-counts">
                    <div class="tv-count is-todo"><b>${counts.todo}</b><span>Te toca a ti</span></div>
                    <div class="tv-count"><b>${counts.wait}</b><span>Esperando</span></div>
                    <div class="tv-count is-next"><b>${counts.next}</b><span>Próximas</span></div>
                </div>
            </header>
            <div class="tv-layout">
                ${isStudent ? `
                <section class="tv-card">
                    <h2>Pedir una tutoría</h2>
                    <form id="solicitar-tutoria-form" class="tv-form" ${UIEvents.attr("submit", function(event,captured){TutoriasView.requestTutoria(event);}, [])}>
                        <label>Profesor
                            <select id="tutoria-profesor" class="tv-input" required>
                                <option value="">${this._professores.length ? 'Elige profesor' : 'No tienes profesores con clase este curso'}</option>
                                ${this._professores.map(p => `<option value="${this._esc(p.id)}">${this._esc(p.firstName)} ${this._esc(p.lastName)}</option>`).join('')}
                            </select>
                        </label>
                        <label>¿De qué quieres hablar?
                            <textarea id="tutoria-notas" class="tv-input" rows="4" placeholder="Cuéntale brevemente el motivo" required></textarea>
                        </label>
                        <button type="submit" class="tv-btn is-primary is-big"><i class="fas fa-paper-plane"></i> Enviar solicitud</button>
                        <small class="tv-muted">Solo salen los profesores que te dan clase este curso.</small>
                    </form>
                </section>` : `
                <section class="tv-card">
                    ${this.renderCalendar()}
                    ${this.renderUpcoming()}
                    ${this.renderAdvisory()}
                </section>`}
                <section class="tv-list">
                    <div class="tv-list-head">
                        <h2>Solicitudes y estados</h2>
                        <select class="tv-input" aria-label="Mostrar" ${UIEvents.attr("change", function(event,captured){TutoriasView.setFilter(this.value);}, [])}>
                            <option value="open" ${this._filter === 'open' ? 'selected' : ''}>Abiertas y próximas</option>
                            <option value="history" ${this._filter === 'history' ? 'selected' : ''}>Historial</option>
                        </select>
                    </div>
                    <div id="tutorias-list-container" class="tv-list">${this.renderTutoriasList(isStudent, !isStudent)}</div>
                </section>
            </div>
        </div>`;
    },

    renderTutoriasList(isStudent) {
        const esc = s => this._esc(s);
        const order = { todo: 0, wait: 1, next: 2, history: 3 };
        const list = this._tutorias.filter(t => (this._bucket(t) === 'history') === (this._filter === 'history'))
            .sort((a, b) => {
                const ba = this._bucket(a), bb = this._bucket(b);
                if (ba !== bb) return order[ba] - order[bb];
                if (ba === 'next') return `${a.confirmedDate} ${a.confirmedTime}`.localeCompare(`${b.confirmedDate} ${b.confirmedTime}`);
                if (ba === 'history') return new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0);
                return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
            });
        if (!list.length) {
            return `<div class="tv-empty"><p><b>${this._filter === 'history' ? 'El historial está vacío' : 'No tienes tutorías abiertas'}</b></p><p>${this._filter === 'history' ? 'Aquí aparecen las tutorías canceladas y las que ya pasaron.' : (isStudent ? 'Pide una con el formulario de la izquierda.' : 'Cuando un alumno te pida tutoría aparecerá aquí.')}</p></div>`;
        }
        const when = (date, time) => `${esc(this._day(date))} a las ${esc(time || '')}`;
        return list.map(t => {
            const bucket = this._bucket(t);
            const id = esc(t.id);
            const pill = t.status === 'cancelled' ? ['is-bad', 'Cancelada'] : t.status === 'confirmed' ? (bucket === 'next' ? ['is-ok', 'Confirmada'] : ['', 'Ya pasó'])
                : bucket === 'todo' ? ['is-warn', 'Te toca a ti'] : ['', 'Esperando'];
            let ask = '', body = '', box = bucket === 'todo' ? 'is-todo' : bucket === 'next' ? 'is-next' : t.status === 'cancelled' ? 'is-cancelled' : '';
            switch (t.status) {
                case 'pending':
                    if (isStudent) ask = 'Esperando a que el profesor proponga un día.';
                    else {
                        ask = 'Propón un día y una hora';
                        body = `<div class="tv-row">
                            <input type="date" id="prop-date-${id}" class="tv-input" aria-label="Día">
                            <input type="time" step="900" id="prop-time-${id}" class="tv-input tv-time" aria-label="Hora">
                            <button type="button" class="tv-btn is-primary" ${UIEvents.attr("click", function(event,captured){TutoriasView.proposeOne(String(captured[0]));}, [t.id])}>Enviar propuesta</button></div>`;
                    }
                    break;
                case 'proposed_1':
                    if (isStudent) {
                        ask = `Te propone el ${when(t.proposedDate, t.proposedTime)}`;
                        body = `<div class="tv-row">
                            <button type="button" class="tv-btn is-primary" ${UIEvents.attr("click", function(event,captured){TutoriasView.respondOne(String(captured[0]),true);}, [t.id])}>Me va bien</button>
                            <button type="button" class="tv-btn" ${UIEvents.attr("click", function(event,captured){TutoriasView.respondOne(String(captured[0]),false);}, [t.id])}>No puedo</button></div>`;
                    } else ask = `Le propusiste el ${when(t.proposedDate, t.proposedTime)}. Esperando su respuesta.`;
                    break;
                case 'rejected_1':
                    if (isStudent) ask = `Dijiste que no podías («${esc(t.rejectionReason || 'sin motivo')}»). Esperando tres alternativas.`;
                    else {
                        ask = `No puede el ${esc(this._day(t.proposedDate))} («${esc(t.rejectionReason || 'sin motivo')}»). Propón tres alternativas`;
                        body = `${[1, 2, 3].map(i => `<div class="tv-row"><span class="tv-opt-label">Opción ${i}</span>
                            <input type="date" id="opt-date-${id}-${i}" class="tv-input" aria-label="Día de la opción ${i}">
                            <input type="time" step="900" id="opt-time-${id}-${i}" class="tv-input tv-time" aria-label="Hora de la opción ${i}"></div>`).join('')}
                            <div class="tv-row"><button type="button" class="tv-btn is-primary" ${UIEvents.attr("click", function(event,captured){TutoriasView.proposeThree(String(captured[0]));}, [t.id])}>Enviar 3 alternativas</button></div>`;
                    }
                    break;
                case 'proposed_3':
                    if (isStudent) {
                        ask = 'Elige una de estas fechas';
                        body = `<div class="tv-row">${(t.options3 || []).map((opt, idx) => `<button type="button" class="tv-btn" ${UIEvents.attr("click", function(event,captured){TutoriasView.respondThree(String(captured[0]),captured[1]);}, [t.id, idx])}>${esc(this._day(opt.date, { weekday: 'short', day: 'numeric', month: 'short' }))} · ${esc(opt.time)}</button>`).join('')}</div>`;
                    } else {
                        ask = 'Le enviaste tres alternativas. Esperando a que elija.';
                        body = `<div class="tv-row">${(t.options3 || []).map(opt => `<span class="tv-pill">${esc(this._day(opt.date, { weekday: 'short', day: 'numeric', month: 'short' }))} · ${esc(opt.time)}</span>`).join('')}</div>`;
                    }
                    break;
                case 'confirmed':
                    ask = when(t.confirmedDate, t.confirmedTime);
                    if (bucket === 'next') body = `<div class="tv-row"><button type="button" class="tv-btn is-danger" ${UIEvents.attr("click", function(event,captured){TutoriasView.cancelTutoria(String(captured[0]));}, [t.id])}>Cancelar cita</button></div>`;
                    break;
                case 'cancelled':
                    ask = `Cancelada: «${esc(t.cancellationReason || 'sin motivo')}»`;
                    break;
            }
            const created = t.createdAt ? new Date(t.createdAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }) : '';
            return `
            <article class="tv-item ${bucket === 'todo' ? 'is-todo' : ''}">
                <div class="tv-item-head">
                    <div><b>${esc(isStudent ? t.professorName : t.studentName)}</b>${created ? `<small>Pedida el ${esc(created)}</small>` : ''}</div>
                    <span class="tv-pill ${pill[0]}">${pill[1]}</span>
                </div>
                <p class="tv-reason"><b>Motivo:</b> ${esc(t.requestNotes)}</p>
                <div class="tv-act ${box}"><b>${ask}</b>${body}</div>
            </article>`;
        }).join('');
    },

    async init() {
        await this.loadTutorias();
        if (AuthManager.isStudent()) {
            try {
                const token = AuthManager.getCsrfToken();
                // Only the professors this student has classes with this academic year.
                const res = await fetch('/api/tutorias/professors', { headers: { 'X-SICAF-CSRF': token } });
                const data = await res.json();
                if (data.success) this._professores = data.professors || [];
            } catch(e) {
                console.error('Error al cargar profesores:', e);
            }
        }
        this._repaint();
    },

    async loadTutorias() {
        try {
            const token = AuthManager.getCsrfToken();
            const res = await fetch('/api/tutorias', { headers: { 'X-SICAF-CSRF': token } });
            const data = await res.json();
            if (data.success) this._tutorias = data.tutorias || [];
        } catch(e) {
            console.error('Error al cargar tutorías:', e);
        }
    },

    async _post(url, body) {
        const token = AuthManager.getCsrfToken();
        const res = await fetch(url, { method: 'POST', headers: { 'X-SICAF-CSRF': token, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        return res.json();
    },

    async requestTutoria(e) {
        if (e) e.preventDefault();
        const select = document.getElementById('tutoria-profesor');
        const notas = document.getElementById('tutoria-notas');
        if (!select || !notas) return;
        const professorId = select.value;
        const professorName = select.options[select.selectedIndex].text;
        const requestNotes = notas.value.trim();
        if (!professorId || !requestNotes) return;
        try {
            const data = await this._post('/api/tutorias', { professorId, professorName, requestNotes });
            if (data.success) {
                Utils.showToast('Solicitud de tutoría enviada', 'success');
                await this.init();
            } else {
                SafeHTML.fire('Error', data.error || 'No se pudo enviar la solicitud', 'error');
            }
        } catch(e) {
            console.error('Error enviando tutoría:', e);
            Utils.showToast('Error al enviar la solicitud', 'error');
        }
    },

    async proposeOne(tutId) {
        const date = document.getElementById(`prop-date-${tutId}`)?.value;
        const time = document.getElementById(`prop-time-${tutId}`)?.value;
        if (!date || !time) {
            SafeHTML.fire('Falta el día o la hora', 'Elige el día y la hora que le propones.', 'warning');
            return;
        }
        try {
            const data = await this._post(`/api/tutorias/${tutId}/proponer-uno`, { date, time });
            if (data.success) {
                Utils.showToast('Propuesta enviada al alumno', 'success');
                await this.init();
            } else {
                SafeHTML.fire('Error', data.error || 'No se pudo enviar la propuesta', 'error');
            }
        } catch(e) {
            console.error('Error proponer uno:', e);
        }
    },

    async respondOne(tutId, accepted) {
        let rejectionReason = null;
        if (!accepted) {
            const inputResult = await SafeHTML.fire({
                title: '¿Por qué no puedes?',
                text: 'Cuéntale brevemente al profesor por qué no te viene bien, para que lo tenga en cuenta:',
                input: 'text',
                inputPlaceholder: 'Ej.: tengo clase de montaje, trabajo por la mañana…',
                inputAttributes: { maxlength: '500' },
                showCancelButton: true,
                confirmButtonText: 'Enviar',
                cancelButtonText: 'Volver',
                confirmButtonColor: '#8E2A21',
                reverseButtons: true
            });
            if (!inputResult.isConfirmed) return;
            rejectionReason = inputResult.value.trim() || 'No especificado';
        }
        try {
            const data = await this._post(`/api/tutorias/${tutId}/responder-uno`, { accepted, rejectionReason });
            if (data.success) {
                Utils.showToast(accepted ? 'Tutoría confirmada' : 'Respuesta enviada al profesor', 'success');
                await this.init();
            } else {
                SafeHTML.fire('Error', data.error || 'No se pudo registrar tu respuesta', 'error');
            }
        } catch(e) {
            console.error('Error responder uno:', e);
        }
    },

    async proposeThree(tutId) {
        const options3 = [];
        for (let i = 1; i <= 3; i++) {
            const dateVal = document.getElementById(`opt-date-${tutId}-${i}`)?.value;
            const timeVal = document.getElementById(`opt-time-${tutId}-${i}`)?.value;
            if (dateVal && timeVal) options3.push({ date: dateVal, time: timeVal });
        }
        if (options3.length < 3) {
            SafeHTML.fire('Faltan opciones', 'Rellena el día y la hora de las tres alternativas.', 'warning');
            return;
        }
        try {
            const data = await this._post(`/api/tutorias/${tutId}/proponer-tres`, { options3 });
            if (data.success) {
                Utils.showToast('Alternativas enviadas al alumno', 'success');
                await this.init();
            } else {
                SafeHTML.fire('Error', data.error || 'No se pudieron enviar las opciones', 'error');
            }
        } catch(e) {
            console.error('Error proponer tres:', e);
        }
    },

    async respondThree(tutId, optionIndex) {
        const confirmResult = await SafeHTML.fire({
            title: '¿Confirmar esta fecha?',
            text: 'La tutoría quedará fijada para el día y la hora que has elegido.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, confirmar',
            cancelButtonText: 'Volver',
            confirmButtonColor: '#0E6B62',
            reverseButtons: true
        });
        if (!confirmResult.isConfirmed) return;
        try {
            const data = await this._post(`/api/tutorias/${tutId}/responder-tres`, { selectedOptionIndex: optionIndex });
            if (data.success) {
                Utils.showToast('Tutoría confirmada', 'success');
                await this.init();
            } else {
                SafeHTML.fire('Error', data.error || 'No se pudo seleccionar la opción', 'error');
            }
        } catch(e) {
            console.error('Error responder tres:', e);
        }
    },

    async cancelTutoria(tutId) {
        const cancelResult = await SafeHTML.fire({
            title: '¿Cancelar esta tutoría?',
            text: 'Indica el motivo. Se avisará a la otra parte y a administración:',
            input: 'textarea',
            inputPlaceholder: 'Ej.: consulta médica, imprevisto de trabajo…',
            showCancelButton: true,
            confirmButtonText: 'Sí, cancelar',
            cancelButtonText: 'Volver',
            confirmButtonColor: '#8E2A21',
            reverseButtons: true
        });
        if (!cancelResult.isConfirmed) return;
        const cancellationReason = cancelResult.value.trim();
        if (!cancellationReason) {
            SafeHTML.fire('Falta el motivo', 'Escribe el motivo para poder cancelar la tutoría.', 'warning');
            return;
        }
        try {
            const data = await this._post(`/api/tutorias/${tutId}/cancelar`, { cancellationReason });
            if (data.success) {
                Utils.showToast('Tutoría cancelada', 'success');
                await this.init();
            } else {
                SafeHTML.fire('Error', data.error || 'No se pudo cancelar la tutoría', 'error');
            }
        } catch(e) {
            console.error('Error al anular tutoría:', e);
        }
    }
};

window.TutoriasView = TutoriasView;
